import { useState } from "react";
import { addListing, publishCheck } from "../../lib/company";
import { ApiError } from "../../lib/api";
import { setListingStatus } from "../../lib/companyApi";
import { useCompany } from "../../lib/companyContext";
import { UpgradeModal } from "./modals";

export type PublishOutcome = "published" | "limit" | "failed";

/**
 * Publishing flow shared by the listings pages: checks the plan limit
 * locally (subscriptions have no API yet) and offers an upgrade, then
 * publishes through PATCH /company/listings/:id/status.
 * Render `modal` somewhere in the page.
 */
export function usePublish() {
  const { state, run, toast } = useCompany();
  const [retry, setRetry] = useState<(() => void) | null>(null);

  /** `then` runs once the listing is live, including after an upgrade + retry. */
  const publish = async (
    listingId: string,
    then?: () => void,
    /** Set on the retry after an upgrade, where `state` still has the old plan. */
    upgraded = false,
  ): Promise<PublishOutcome> => {
    const check = upgraded ? { ok: true as const } : publishCheck(state, listingId);
    if (!check.ok) {
      if (check.reason === "limit") {
        setRetry(() => () => void publish(listingId, then, true));
        return "limit";
      }
      toast.warning(check.message);
      return "failed";
    }

    try {
      const { listing, units, message } = await setListingStatus(listingId, "published");
      run(addListing, listing, units);
      toast.success(message);
      then?.();
      return "published";
    } catch (err) {
      // 403: company not verified yet; the listing stays a draft.
      if (err instanceof ApiError && err.status === 403) toast.warning(err.message);
      else toast.error(err);
      return "failed";
    }
  };

  const modal = retry && (
    <UpgradeModal onClose={() => setRetry(null)} onUpgraded={() => setTimeout(retry, 0)} />
  );

  return { publish, modal };
}
